/**
 * Ensures that a minimum amount of time has passed since a given start time.
 *
 * This is useful for UI loading states where you want a spinner or
 * in-progress indicator to be visible for at least a fixed duration,
 * including the time spent awaiting an async operation.
 *
 * Behavior:
 * - If the elapsed time is greater than or equal to minMs, this resolves immediately.
 * - If the elapsed time is less than minMs, this waits for the remaining time.
 *
 * @param startTime - Timestamp in milliseconds when the operation started.
 *                    Typically obtained from Date.now().
 * @param minMs - Minimum total duration in milliseconds.
 *
 * @returns A Promise that resolves once the minimum duration has been met.
 *
 * @example
 * const startTime = Date.now()
 * setIsFetching(true)
 *
 * const result = await apiCall()
 *
 * await waitMinDuration(startTime, 500)
 *
 * setIsFetching(false)
 */
export const waitMinDuration = async (
    startTime: number,
    minMs: number
): Promise<void> => {
    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, minMs - elapsed)

    if (remaining > 0) {
        await new Promise<void>(resolve => {
            setTimeout(resolve, remaining)
        })
    }
}
